import React, { useEffect, useState } from "react";
import "../styles/home.css";
import "../styles/table.css";
import "../styles/theme.css";
import Layout from "./Layout";
import DropdownComponent from "./display/DropDownComponent";
import { getDailyReconDips, getDailyReconSnapshot, getSiteNames } from "../functions/api/details";
import { serialize } from "v8";
import SmallTableComponent from "./display/SmallTable";
import { DipSummaryRow } from "../interfaces/interfaces";
import { Rowing } from "@mui/icons-material";
import DescriptionList from "./display/DetailsComponent";

const getToday = (): string => {
    const date = new Date();
    date.setDate(date.getDate() - 1); // Load for previous day on start, otherwise next morning dip is 0
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
};

const ReconDailySnapshotComponent: React.FC = () => {
    // Recon Daily Snapshot data handling
    const [reconDailySnapshotData, setReconDailySnapshotData] = useState<any[]>([]);
    const [selectedCurrentDate, setSelectedCurrentDate] = useState<string>(getToday);
    const [code, setSelectedCode] = useState<string>('Lusaka');
    const [siteOptions, setSiteOptions] = useState<any[]>([]);
    const [dipSummary, setDipSummary] = useState<any[]>([]);

    // Loading display - TODO: Replace with spinner
    const [loading, setLoading] = useState<boolean>(true);


    // Dropdown for sites
    const populateSiteItems = async() =>{
        // Fetching the site names
        const data =  await getSiteNames();
        if (Array.isArray(data)) {
            setSiteOptions(data);
        } else {
            setSiteOptions([]);
        }

    };


    function getDipTotals(data: DipSummaryRow[]): DipSummaryRow {
      const dipDate = data.length > 0 ? data[0].dip_date : '';
    
      return {
        tank_name: 'Total',
        dip_date: dipDate,
        morning_dip_volume: data.reduce((sum, row) => sum + (row.morning_dip_volume || 0), 0),
        evening_dip_volume: data.reduce((sum, row) => sum + (row.evening_dip_volume || 0), 0),
        next_morning_dip_volume: data.reduce((sum, row) => sum + (row.next_morning_dip_volume || 0), 0),
      };
    }
    
    const dipTotals = getDipTotals(dipSummary);
 
    const dipSummaryWithoutDate = [...dipSummary, dipTotals].map(({ dip_date, ...rest }) => rest);

    const loadDailyReconSnapshot = async (code: string, date: string) => {
        setLoading(true);
        // console.log("GOt HEREERERERER")
        const data = await getDailyReconSnapshot(code, date);
        const dipsData = await getDailyReconDips(code, date);
        console.log(dipsData)

        if (Array.isArray(dipsData)) {
        setDipSummary(dipsData);
        } else {
        setDipSummary([]);
        }

        if (Array.isArray(data)) {
            setReconDailySnapshotData(data);
        } else {
            setReconDailySnapshotData([]);
        }

        setLoading(false);
        // console.log(data)
    };


    useEffect(() => {
        populateSiteItems();
        loadDailyReconSnapshot(code, selectedCurrentDate);
    }, []);

    // Convert raw integer back into timestamp
    function minutesToTimestamp(seconds: number): string {
      const hrs = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

        // Split the data by concern
    const stockSummary = reconDailySnapshotData.map(row => ({
      "Date": row.slc_date,
      "Start Dip Time": minutesToTimestamp(row.slc_start_dip_time),
      "Start Gross": row.slc_start_gross,
      "End Dip Time": minutesToTimestamp(row.slc_end_dip_time),
      "End Gross": row.slc_end_gross
    }));

    const dispensed = reconDailySnapshotData[0]?.total_dispensed_volume ?? null;
    
    const gainLossData = reconDailySnapshotData.map(row => ({
      "Date": row.slc_date,
      "Day Gross Tank Difference": dipTotals.evening_dip_volume - dipTotals.morning_dip_volume, // Diverting from FS original definitions of
      "Shift Variance": dipTotals.next_morning_dip_volume - dipTotals.evening_dip_volume, // Variance term
      "Total Gross Received":
        row.rec_gross_diff === 0 ? "No receipt" : row.rec_gross_diff,
      "Total Dispensed": dispensed,
      "Total GST Received": row.rec_GST_diff === 0 ? "No receipt" : row.rec_GST_diff,
      "Gain or Loss": 
      (
        (dipTotals.evening_dip_volume - dipTotals.morning_dip_volume) 
        - row.rec_GST_diff
        + dispensed
        + (dipTotals.next_morning_dip_volume - dipTotals.evening_dip_volume)
      ), // row.gain_or_loss,  Adjusted Gain according to Korridor Definition
      "Percentage":  (((
                          (dipTotals.evening_dip_volume - dipTotals.morning_dip_volume) 
                          - row.rec_GST_diff
                          + dispensed
                          + (dipTotals.next_morning_dip_volume - dipTotals.evening_dip_volume)
                        ) / dispensed) * 100).toFixed(2) + "%" // Make a 2 decimal point percentage of the Gain or Loss
    }));
    
    const outflows = reconDailySnapshotData.map(row => ({
      "Date": row.slc_date,
      "Total Gross Outflow": row.outfl_gross_diff
    }));
    
    const handleCurrentDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setSelectedCurrentDate(newDate);
        loadDailyReconSnapshot(code, newDate);
    };


    const handleDropdownChange = (newCode: string) => {
        setSelectedCode(newCode);
        loadDailyReconSnapshot(newCode, selectedCurrentDate);
    };


    return (
        <Layout>
            <div className="home-container" style={{ overflowX: "auto" }}>
                <h2 className="home-header">Daily Recon Snapshot</h2>
                <DescriptionList text="
                                This report shows a snapshot of the day in terms of what was received, how the tanks behaved, how much was disepensed and what is the gain/loss.
                                /nYou can choose which site and which date you want to analyse.
                                /nThe Day Gross Tank Difference is derived from total Evening dip volume minus total Morining dip volume.
                                /nThe Shift Variance is derived from the total Next morning dip volume minus the total current Evening dip volume.
                                /nThe final Gain or Loss is then calculated by: Day Gross Tank Difference + Shift Variance - Total Gross Received - Total Dispensed.
                                /n* Note the gain is calculated with all Gross values, however, the GST for the receive amount is displayed here.
                                /n* Note the if there were a lot of standalone transactions that occurred previously, and the dispensers switch over to console mode, the standalone transactions will reflect later and will reflect in the gain/loss calculation.
                                " 
                />
                
                {/* Daily Recon Snapshot Data Section */}
                    <div  className="dropdown">
                        <DropdownComponent
                            options={siteOptions}
                            value={code}
                            onChange={handleDropdownChange}
                            label="Choose code"
                        />
                        {code && <p className="small">You selected: {code}</p>}
                    </div>

                    {/* RECEIVE OUTFLOW: From Date */}
                    <div style={{ marginBottom: "1rem" }}>
                        <label style={{ marginRight: "0.5rem" }}>Date:</label>
                        <div className="date-picker-wrapper">

                    {/* Left Arrow */}
                    <button className="date-arrow" onClick={() => {
                            const prev = new Date(selectedCurrentDate);
                            prev.setDate(prev.getDate() - 1);
                            handleCurrentDateChange({
                                target: { value: prev.toISOString().slice(0, 10) }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                    >←
                    </button>

                        <input
                            type="date"
                            value={selectedCurrentDate}
                            onChange={handleCurrentDateChange}
                            style={{ padding: "0.4rem" }}
                        />

                    {/* Right Arrow */}
                    <button className="date-arrow" onClick={() => {
                            const next = new Date(selectedCurrentDate);
                            next.setDate(next.getDate() + 1);
                            handleCurrentDateChange({
                                target: { value: next.toISOString().slice(0, 10) }
                            } as React.ChangeEvent<HTMLInputElement>);
                        }}
                    >→
                    </button>
                    </div>
          
         {loading ? (
           <p>Loading...</p>
         ) : (
            <>
             <h3 className="normal-heading">Stock Summary</h3>
             {stockSummary.length > 0 ? (
               <div className="table-scroll-wrapper">
                 <div className="table-scroll-inner">
                   <SmallTableComponent data={stockSummary} showSearchControls={false}/>
                 </div>
               </div>
             ) : (
               <p>No stock summary data found for {selectedCurrentDate}.</p>
             )}

              <h3 className="normal-heading">Gross Dip Summary</h3>
              {dipSummary.length > 0 ? (
                <div className="table-scroll-wrapper">
                  <div className="table-scroll-inner">
                    <SmallTableComponent 
                    data={dipSummaryWithoutDate} 
                    middleColumnKey={[
                      "morning_dip_time",
                      "evening_dip_time",
                      "next_morning_dip_time",
                    ]}
                    thresholds={{
                      morning_dip_time: { maxTime: "06:00" },  
                      morning_dip_level_mm: { min: 250 },
                      evening_dip_time: { minTime: "23:00" },  
                      evening_dip_level_mm: { min: 250 },
                      next_morning_dip_time: { maxTime: "06:00" },  
                      next_morning_dip_level_mm: { min: 250 },         
                    }}
                    showSearchControls={false}
                    rowClassName={(row) => row.tank_name === 'Total' ? 'summary-row' : ''}
                    
                    />
                  </div>
                </div>
              ) : (
                <p>No dip summary data found for {selectedCurrentDate}.</p>
              )}

             <h3 className="normal-heading">Site Gain/Loss</h3>
             {gainLossData.length > 0 ? (
               <div className="table-scroll-wrapper">
                 <div className="table-scroll-inner">
                   <SmallTableComponent data={gainLossData} showSearchControls={false}/>
                 </div>
               </div>
             ) : (
               <p>No gain/loss data found for {selectedCurrentDate}.</p>
             )}

             <h3 className="normal-heading">Site Outflows</h3>
             {outflows.length > 0 ? (
               <div className="table-scroll-wrapper">
                 <div className="table-scroll-inner">
                   <SmallTableComponent data={outflows} showSearchControls={false}/>
                 </div>
               </div>
             ) : (
               <p>No outflow data found for {selectedCurrentDate}.</p>
             )}
           </>
         )}
        </div>
       </div>         
      </Layout>
    );
};

export default ReconDailySnapshotComponent;
